/* =========================================================================
 * Kkumi · Cart codes — window.KKCartCodes (cart v14)
 * -------------------------------------------------------------------------
 * The one place the cart page applies, replaces and removes discount codes,
 * shared by the order summary (promo field + saved offers) and the points
 * module (apply a redeemed reward code). Reward codes are ordinary Shopify
 * discount codes, so everything goes through /cart/update.js `discount`.
 *
 *   KKCartCodes.applied(cart)     → the order's code(s) on the cart, riders left
 *                                   out (lower-case compare; Functions-based
 *                                   rewards land as discount *applications*,
 *                                   not in discount_codes, so both are read)
 *   KKCartCodes.apply(code)       → Promise<{ ok, state, previous, message?, reason? }>
 *                                   on a failure, `message` is Kkumi Hub's reason in
 *                                   the theme's words when it knows one (why() below)
 *   KKCartCodes.why(code, cart)   → Promise<{ reason, message } | null>
 *   KKCartCodes.remove(code)      → Promise<state>
 *   KKCartCodes.cart()            → the latest cart JSON we've seen
 *   KKCartCodes.addItems(items)   → /cart/add.js that rejects on refusal
 *   KKCartCodes.onCart(cart)      → every code on the cart, applicable or not
 *   KKCartCodes.addRider(code) / isRider(code)
 *
 * One code per order: applying replaces whatever code is on the cart. Shopify
 * answers 200 even when a code can't apply (it comes back applicable:false)
 * and has by then dropped the previous code — so a failed apply puts the
 * previous code(s) back. A code is never removed silently.
 *
 * Riders are the exception (30 Oct 2026): the member's free-shipping codes,
 * which combine with the order's code, and which Shopify may report as not
 * applicable until checkout prices shipping. Applying a rider adds it beside
 * everything on the cart (it replaces nothing, and it counts as applied once
 * it is on the cart); applying or removing any other code keeps every rider
 * that is on the cart. The order summary and the points module register them
 * from /codes (reward_type free_shipping); a just-redeemed one is added first.
 * Registering only ever adds (a reload of /codes that hasn't caught up with a
 * fresh redemption must not turn its code back into one that gets replaced).
 *
 * Every mutation asks for the sections Dawn's quantity change re-renders,
 * paints them and publishes `cartUpdate` (source 'cart-items'), so the items,
 * summary, shipping panel and points module all refresh from one response.
 * ========================================================================= */
(function () {
  'use strict';
  if (window.KKCartCodes) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var latest = null;

  function lower(s) { return String(s || '').trim().toLowerCase(); }

  // Riders (see the header): lower-cased codes.
  var riders = {};
  function isRider(code) { return !!riders[lower(code)]; }
  function addRider(code) { if (lower(code)) riders[lower(code)] = true; }
  // Every code on the cart, applicable or not, as Shopify holds it.
  function onCart(cart) {
    cart = cart || latest;
    var out = [];
    ((cart && cart.discount_codes) || []).forEach(function (dc) {
      if (dc && lower(dc.code) && !out.some(function (c) { return lower(c) === lower(dc.code); })) out.push(String(dc.code).trim());
    });
    return out;
  }
  function ridersOn(cart) { return onCart(cart).filter(isRider); }
  // A `discount` value for /cart/update.js: each code once.
  function join(list) {
    var seen = {};
    return list.filter(function (c) { var k = lower(c); if (!k || seen[k]) return false; seen[k] = true; return true; }).join(',');
  }

  function applied(cart) {
    cart = cart || latest;
    var out = [];
    var seen = {};
    function add(code) {
      var k = lower(code);
      if (!k || seen[k] || riders[k]) return;
      seen[k] = true;
      out.push(String(code).trim());
    }
    if (!cart) return out;
    (cart.discount_codes || []).forEach(function (dc) { if (dc && dc.applicable) add(dc.code); });
    (cart.cart_level_discount_applications || []).forEach(function (a) { if (a && a.type === 'discount_code') add(a.title); });
    (cart.items || []).forEach(function (it) {
      (it.line_level_discount_allocations || []).forEach(function (al) {
        var app = al && al.discount_application;
        if (app && app.type === 'discount_code') add(app.title);
      });
    });
    return out;
  }

  function has(cart, code) {
    var want = lower(code);
    return applied(cart).some(function (c) { return lower(c) === want; });
  }

  function cartItems() {
    var c = document.querySelector('cart-items');
    return c && typeof c.getSectionsToRender === 'function' ? c : null;
  }

  function post(path, body, withSections) {
    var c = withSections ? cartItems() : null;
    if (c) {
      body.sections = c.getSectionsToRender().map(function (s) { return s.section; });
      body.sections_url = window.location.pathname;
    }
    return fetch(ROOT + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return null; }).then(function (j) {
        if (!r.ok) {
          var e = new Error((j && (j.description || j.message)) || ('HTTP ' + r.status));
          e.cartError = (j && j.description) || null;
          throw e;
        }
        return j;
      });
    });
  }

  function paint(state) {
    if (!state) return;
    latest = state;
    var c = cartItems();
    if (c && state.sections) {
      c.getSectionsToRender().forEach(function (section) {
        var el = document.getElementById(section.id);
        if (!el || !state.sections[section.section]) return;
        var target = el.querySelector(section.selector) || el;
        target.innerHTML = c.getSectionInnerHTML(state.sections[section.section], section.selector);
      });
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
    }
  }

  function apply(code) {
    code = String(code || '').trim();
    var previous = applied();
    var before = onCart();
    var keep = ridersOn().filter(function (r) { return lower(r) !== lower(code); });
    // A rider joins everything on the cart; any other code replaces the order's code and keeps the riders.
    var rider = isRider(code);
    var list = rider ? before.concat([code]) : [code].concat(keep);
    return post('cart/update.js', { discount: join(list) }, true).then(function (state) {
      // A rider counts once it is on the cart: Shopify may call it not applicable until checkout.
      var ok = rider ? onCart(state).some(function (c) { return lower(c) === lower(code); }) : has(state, code);
      if (ok) {
        paint(state);
        return { ok: true, state: state, previous: previous };
      }
      // Not applicable: put back what was there, then report the failure.
      var restore = rider ? join(before) : join(previous.filter(function (p) { return lower(p) !== lower(code); }).concat(keep));
      return post('cart/update.js', { discount: restore }, true).then(function (back) {
        paint(back);
        return explained({ ok: false, state: back, previous: previous, attempted: state }, code, state);
      }, function () {
        paint(state);
        return explained({ ok: false, state: state, previous: previous, attempted: state }, code, state);
      });
    });
  }

  // ── Why a code didn't apply (owner, 3 Nov 2026: "The error message is worse than useless") ──
  // Shopify's cart only says "not applicable". Kkumi Hub knows why for the codes it made (the member's
  // welcome code: signed out, another account, a missing pack, under the minimum, used, expired), so a
  // failed apply asks POST /apps/rewards/code-help with the attempted cart and words the answer from
  // Theme settings → Discount code help (snippets/kkumi-code-help-copy.liquid). An unknown reason, or
  // no answer, leaves the caller's own message. Never blocks: 4 seconds at most.
  var helpCopy = null;
  function copyFor(reason) {
    if (!helpCopy) {
      helpCopy = {};
      var el = document.querySelector('script[data-kk-code-help]');
      try { if (el) helpCopy = JSON.parse(el.textContent) || {}; } catch (e) { helpCopy = {}; }
    }
    return helpCopy[reason] || '';
  }
  function money(cents) {
    var M = window.KKMoney;
    // KKMoney is head-loaded on every page; without it the amount is left out rather than mis-formatted.
    return M && M.fromShopCents ? M.fromShopCents(cents, { trim: true }) : '';
  }
  function words(code, res) {
    var p = (res && res.params) || {};
    var reason = res && res.reason;
    if (reason === 'sleeves_missing' && !p.items) reason = 'sleeves_missing_any';
    var t = copyFor(reason);
    if (!t) return '';
    var date = '';
    if (p.date) {
      var d = new Date(p.date + 'T00:00:00');
      try { date = d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }); } catch (e) { date = p.date; }
    }
    var map = {
      code: code, items: p.items || '', want: p.want || '', n: p.n != null ? String(p.n) : '', date: date,
      short: p.short_cents != null ? money(p.short_cents) : '', min: p.min_cents != null ? money(p.min_cents) : ''
    };
    Object.keys(map).forEach(function (k) { t = t.split('[' + k + ']').join(map[k]); });
    return t;
  }
  function linesOf(cart) {
    var M = window.KKMoney;
    return ((cart && cart.items) || []).map(function (it) {
      var local = it.original_price != null ? it.original_price : it.price;
      var shop = M && M.toShopCents ? M.toShopCents(local) : local;
      return { variant_id: it.variant_id, product_id: it.product_id, quantity: it.quantity, price_cents: shop == null ? null : shop };
    });
  }
  function why(code, cart) {
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 4000) : null;
    return fetch(ROOT + 'apps/rewards/code-help', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ code: code, lines: linesOf(cart || latest) }),
      signal: ctrl ? ctrl.signal : undefined
    }).then(function (r) { return r.ok ? r.json() : null; }).then(function (res) {
      if (!res || !res.reason || res.reason === 'unknown') return null;
      var message = words(code, res);
      return message ? { reason: res.reason, message: message } : null;
    }).catch(function () { return null; }).then(function (out) { if (timer) clearTimeout(timer); return out; });
  }
  function explained(result, code, attempted) {
    return why(code, attempted).then(function (w) {
      if (w) { result.message = w.message; result.reason = w.reason; }
      return result;
    });
  }

  function remove(code) {
    // The order's code and every rider stay, less the one removed.
    var keep = join(applied().concat(ridersOn()).filter(function (c) { return lower(c) !== lower(code); }));
    return post('cart/update.js', { discount: keep }, true).then(function (state) { paint(state); return state; });
  }

  // /cart/add.js that rejects on a refusal (422/404) or a 200 that didn't
  // actually take every variant asked for.
  function addItems(items) {
    return post('cart/add.js', { items: items }, false).then(function (res) {
      var list = (res && res.items) ? res.items : (res && res.variant_id != null ? [res] : []);
      var got = list.map(function (i) { return String(i.variant_id != null ? i.variant_id : i.id); });
      var missing = items.filter(function (w) { return got.indexOf(String(w.id)) === -1; });
      if (missing.length) throw new Error('cart_add_incomplete');
      return res;
    });
  }

  function seed() {
    var el = document.querySelector('script[data-kkc-cart-json]');
    if (!el) return;
    try { latest = JSON.parse(el.textContent); } catch (e) {}
  }

  window.KKCartCodes = {
    applied: applied,
    has: has,
    apply: apply,
    remove: remove,
    addItems: addItems,
    paint: paint,
    cart: function () { return latest; },
    onCart: onCart,
    why: why,
    isRider: isRider,
    addRider: addRider
  };

  seed();
  function track() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var cart = event && event.cartData;
      if (cart && cart.items) { latest = cart; return; }
      fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          latest = c;
          document.dispatchEvent(new CustomEvent('kkc:cart', { detail: { cart: c } }));
        })
        .catch(function () {});
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { if (!latest) seed(); track(); });
  else track();
})();
